import { buildRetailSaleReceiptTemplate as buildRetailSaleReceiptHtml } from '../utils/retail-sale-receipt-template'
import { buildRetailSaleShiftReportTemplate } from '../utils/retail-sale-shift-report-template'

/**
 * Show the current receipt layout with print dimensions without saving or printing.
 *
 * @param {Object} receiptData Receipt builder data.
 * @returns {boolean} Whether the preview window opened.
 */
export const previewRetailSaleReceipt = receiptData => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return false

  const previewWindow = window.open('', '_blank', 'width=420,height=720')
  if (!previewWindow) return false

  const html = buildRetailSaleReceiptHtml(receiptData).replace(
    '</head>',
    '<style>@media screen{html,body{width:80mm!important;margin:0 auto!important;padding:0!important}.receipt-paper{width:80mm!important;zoom:1!important}.receipt,.receipt *{font-size:12px!important}.receipt-store{font-size:14px!important}.grand-total>div:first-child strong:last-child,.grand-total-riel strong{font-size:16px!important}}</style></head>'
  )
  previewWindow.document.write(html)
  previewWindow.document.close()
  previewWindow.focus()
  return true
}

/**
 * Open a print window during the user click so browser popup protection does
 * not block the receipt after the asynchronous save completes.
 *
 * @returns {Window|null} Prepared print window.
 */
export const openRetailSaleReceiptWindow = () => {
  if (typeof window === 'undefined') return null

  const printWindow = window.open('', '_blank', 'width=420,height=720')
  if (!printWindow) return null

  printWindow.document.write('<!doctype html><title>Preparing receipt...</title>')
  printWindow.document.close()
  return printWindow
}

/**
 * Render and print a saved retail receipt in the local browser.
 *
 * @param {Window|null} printWindow Prepared browser print window.
 * @param {Object} receiptData Receipt builder data.
 * @returns {boolean} Whether the receipt was sent to the browser printer.
 */
export const printRetailSaleReceipt = (printWindow, receiptData) => {
  if (!printWindow || printWindow.closed) return false

  printWindow.document.open()
  printWindow.document.write(buildRetailSaleReceiptHtml(receiptData))
  printWindow.document.close()
  printWindow.focus()
  printWindow.onafterprint = () => printWindow.close()
  const printReceipt = () => printWindow.print()
  const fontsReady = printWindow.document.fonts && printWindow.document.fonts.ready
  if (fontsReady) {
    fontsReady.then(printReceipt)
  } else {
    window.setTimeout(printReceipt, 150)
  }
  return true
}

/**
 * Close a prepared receipt window when saving the sale fails.
 *
 * @param {Window|null} printWindow Prepared browser print window.
 * @returns {void}
 */
export const closeRetailSaleReceiptWindow = printWindow => {
  if (printWindow && !printWindow.closed) printWindow.close()
}

/**
 * Render and print a receipt only after the sale has been saved.
 *
 * @param {Object} receiptData Receipt builder data.
 * @returns {boolean} Whether the hidden print frame was created.
 */
export const printRetailSaleReceiptAfterSave = receiptData => {
  if (typeof window === 'undefined' || !document.body) return false

  const printFrame = document.createElement('iframe')
  printFrame.setAttribute('aria-hidden', 'true')
  printFrame.style.position = 'fixed'
  printFrame.style.right = '0'
  printFrame.style.bottom = '0'
  printFrame.style.width = '0'
  printFrame.style.height = '0'
  printFrame.style.border = '0'
  printFrame.style.visibility = 'hidden'
  document.body.appendChild(printFrame)

  const printWindow = printFrame.contentWindow
  printWindow.document.open()
  printWindow.document.write(buildRetailSaleReceiptHtml(receiptData))
  printWindow.document.close()
  const printReceipt = () => {
    printWindow.focus()
    printWindow.print()
    window.setTimeout(() => printFrame.remove(), 1000)
  }
  const fontsReady = printWindow.document.fonts && printWindow.document.fonts.ready
  if (fontsReady) {
    fontsReady.then(printReceipt)
  } else {
    window.setTimeout(printReceipt, 150)
  }

  return true
}

/**
 * Print the close-shift report as HTML in a hidden browser print frame.
 *
 * @param {Object} reportData Shift summary, cashier context, and labels.
 * @returns {Promise<boolean>} Whether the print dialog was opened and closed.
 */
export const printRetailSaleShiftReport = reportData => {
  if (typeof window === 'undefined' || !document.body) return Promise.resolve(false)

  return new Promise(resolve => {
    const printFrame = document.createElement('iframe')
    printFrame.setAttribute('aria-hidden', 'true')
    printFrame.style.position = 'fixed'
    printFrame.style.right = '0'
    printFrame.style.bottom = '0'
    printFrame.style.width = '0'
    printFrame.style.height = '0'
    printFrame.style.border = '0'
    printFrame.style.visibility = 'hidden'
    document.body.appendChild(printFrame)

    const printWindow = printFrame.contentWindow
    let finished = false
    const finish = printed => {
      if (finished) return
      finished = true
      printWindow.removeEventListener('afterprint', afterPrint)
      printFrame.remove()
      resolve(printed)
    }
    const afterPrint = () => finish(true)

    try {
      printWindow.document.open()
      printWindow.document.write(buildRetailSaleShiftReportTemplate(reportData))
      printWindow.document.close()
      printWindow.addEventListener('afterprint', afterPrint)

      const printReport = () => {
        try {
          printWindow.focus()
          printWindow.print()
        } catch (error) {
          finish(false)
        }
      }
      const fontsReady = printWindow.document.fonts && printWindow.document.fonts.ready
      if (fontsReady) {
        fontsReady.then(printReport, printReport)
      } else {
        window.setTimeout(printReport, 150)
      }
    } catch (error) {
      finish(false)
    }
  })
}

export default {
  previewRetailSaleReceipt,
  openRetailSaleReceiptWindow,
  printRetailSaleReceipt,
  closeRetailSaleReceiptWindow,
  printRetailSaleReceiptAfterSave,
  printRetailSaleShiftReport
}
